import clsx from "clsx";
import { Namespace, TFunction } from "i18next";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";

import { FCC } from "@/types";

type Rate = {
  name: string;
  description: string;
  price: string;
  discountPrice: string;
};

const calculateDiscountPrice = (tribeDiscount: number, price: number) => {
  return price - (price * tribeDiscount) / 100;
};

export const getFriendsWithBenefitsRates = ({
  t,
  tribeDiscount,
}: {
  t: TFunction<Namespace, undefined, Namespace>;
  tribeDiscount: number;
}): Rate[] => [
  {
    name: t("friendsWithBenefits.welcome.flatMobileSwiss"),
    description: t("friendsWithBenefits.welcome.textFlatMobileSwiss"),
    price: "CHF 13.00",
    discountPrice: `CHF ${calculateDiscountPrice(tribeDiscount, 13).toFixed(2)}`,
  },
  {
    name: t("friendsWithBenefits.welcome.flatMobile"),
    description: t("friendsWithBenefits.welcome.textFlatMobile"),
    price: "CHF 18.00",
    discountPrice: `CHF ${calculateDiscountPrice(tribeDiscount, 18).toFixed(2)}`,
  },
  {
    name: t("friendsWithBenefits.welcome.flatMobilePlus"),
    description: t("friendsWithBenefits.welcome.textFlatMobilePlus"),
    price: "CHF 25.00",
    discountPrice: `CHF ${calculateDiscountPrice(tribeDiscount, 25).toFixed(2)}`,
  },
];

export const FriendsWithBenefitsRates: FCC<{
  tribeDiscount: number;
  isMobile?: boolean;
}> = ({ tribeDiscount, children, isMobile }) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        {!isMobile ? (
          <>
            <hr className="w-8 rounded-sm border-1 border-secondary-100" />

            <h4 className="text-base text-primary-100">
              {t("friendsWithBenefits.welcome.rates")}
            </h4>
          </>
        ) : (
          <h2 className="mb-2 text-secondary-100">
            {t("friendsWithBenefits.welcome.rates")}
          </h2>
        )}
      </div>
      <div
        className={`grid ${tribeDiscount > 0 ? "grid-cols-[1fr,auto,auto]" : "grid-cols-[1fr,auto]"} gap-x-8 text-sm`}
      >
        <div
          className={`${tribeDiscount > 0 ? "col-span-3" : "col-span-2"} mb-4 grid ${tribeDiscount > 0 ? "grid-cols-[1fr,auto,auto]" : "grid-cols-[1fr,auto]"} gap-x-8 text-primary-100`}
        >
          <div className="font-normal">
            {t("friendsWithBenefits.welcome.rate")}
          </div>
          <div className="text-left font-normal">
            {t("friendsWithBenefits.welcome.price")}
          </div>
          {tribeDiscount > 0 && (
            <div className="text-left font-normal">
              {t("friendsWithBenefits.welcome.discount", {
                discount: tribeDiscount,
              })}
            </div>
          )}
        </div>
        {getFriendsWithBenefitsRates({ t, tribeDiscount }).map(
          (rate, index) => (
            <Fragment key={index}>
              <div className="py-2">
                <div className="text-base">{rate.name}</div>
                <div className="text-xs">{rate.description}</div>
              </div>
              <div
                className={clsx(
                  "self-start py-2 text-left text-base",
                  tribeDiscount > 0 ? "line-through" : "font-semibold",
                )}
              >
                {rate.price}
              </div>
              {tribeDiscount > 0 && (
                <div className="self-start py-2 text-left text-base font-semibold">
                  {rate.discountPrice}
                </div>
              )}
            </Fragment>
          ),
        )}
      </div>
      <div className="flex justify-center">{children}</div>
    </div>
  );
};
